import { useEffect, useState, useRef, useContext } from 'react';
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faAdd, faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { GlobalContext } from "components/GlobalContext";

import './styles/DPSEntitySelector.scss';

export interface IDPSEntitySelectorProps<T, U> {
    entityService: any;
    entityTypeName: string;
    entityFilterSearchPropName: keyof U;
    generateEntityTitle: (data: T) => string;
    entityChangeHandler: (data?: T) => void;
    onClickExternalHandler: (data?: T) => void;
    onClickAddNewHandler?: () => void;
    selectedEntityRef?: T;
    defaultEntityFilter?: U;
}

const DPSEntitySelector = <T, U>(props: IDPSEntitySelectorProps<T, U>) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const [entityFilter, setEntityFilter] = useState<U>();
    const [entities, setEntities] = useState<T[]>([]);
    const [filterPhrases, setFilterPhrases] = useState<string[]>();
    const [showEntityChooser, setShowEntityChooser] = useState(false);

    let debounceId = 0;

    useEffect(
        () => {
            (async () => {
                if (entityFilter) {
                    const accessToken = await globalContext.getAccessTokenAsync();
                    const result = await props.entityService.GetAllAsync(accessToken, 0, 20, entityFilter);

                    if (result.records != null) {
                        // @ts-ignore
                        setEntities(result.records);
                    }
                    else {
                        globalContextRef.current.showErrorMessage(`Unable to load ${props.entityTypeName}(s)`);
                    }
                }
            })();
        },
        [entityFilter]);

    const showSelectableEntity = (entity?: T) => (
        <Row className="align-items-end">
            <Col className="dynamiceObjectSelectorButton">
                {
                    !showEntityChooser &&
                    <Form.Group
                        className="p-1"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                            if (globalContext.roles.some(r => r === "Web.Admin")) {
                                // @ts-ignore
                                setEntityFilter({ ...entityFilter, ...(props.defaultEntityFilter || {}) });
                                setShowEntityChooser(!showEntityChooser);
                            }
                        }} >
                        <Form.Label>
                            {props.entityTypeName}
                            {
                                props.selectedEntityRef &&
                                <>
                                    &nbsp;
                                    (<span
                                        style={{ cursor: "pointer", fontWeight: "bold" }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            props.onClickExternalHandler(props.selectedEntityRef);
                                        }}>
                                        <FontAwesomeIcon icon={faUpRightFromSquare as IconProp} />
                                        &nbsp;View
                                    </span>)
                                </>
                            }
                            {
                                !props.selectedEntityRef && props.onClickAddNewHandler &&
                                <>
                                    &nbsp;
                                    (<span
                                        style={{ cursor: "pointer", fontWeight: "bold" }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (props.onClickAddNewHandler) {
                                                props.onClickAddNewHandler();
                                            }
                                        }}>
                                        <FontAwesomeIcon icon={faAdd as IconProp} />
                                        &nbsp;Add New
                                    </span>)
                                </>
                            }
                        </Form.Label>
                        <h2>{props.selectedEntityRef ? props.generateEntityTitle(props.selectedEntityRef) : "None Selected"}</h2>
                    </Form.Group>
                }

                <div>
                    {
                        showEntityChooser &&
                        <>
                            <h3>Choose a {props.entityTypeName}</h3>
                            <Row className="align-items-end">
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>{props.entityTypeName}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            autoComplete="none"
                                            onChange={
                                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setFilterPhrases(e.target.value.split(","));
                                                    window.clearTimeout(debounceId);
                                                    debounceId = window.setTimeout(() => {
                                                        let newFilter: U = { ...entityFilter } as U;
                                                        // @ts-ignore
                                                        newFilter[props.entityFilterSearchPropName as keyof U] = filterPhrases;
                                                        setEntityFilter(newFilter);
                                                    }, 500);
                                                }
                                            }
                                            value={filterPhrases?.join(",") || ""}
                                        /><br />
                                        <strong>Click a {props.entityTypeName} to select.</strong>
                                        <Table striped bordered hover size="sm" className="m-1">
                                            <tbody>
                                                {
                                                    !entities || entities.length < 1 &&
                                                    <tr><td>No matches.</td></tr>
                                                }
                                                {
                                                    entities.map(
                                                        (entry, index) => <tr
                                                            key={index}
                                                            style={{ cursor: "pointer" }}
                                                            onClick={() => {
                                                                props.entityChangeHandler(entry);
                                                                setShowEntityChooser(false);
                                                            }}><td>{props.generateEntityTitle(entry)}</td></tr>
                                                    )
                                                }

                                            </tbody>
                                        </Table><br />
                                        <Button
                                            className="mb-1"
                                            style={{ width: "100%" }}
                                            onClick={
                                                () => setShowEntityChooser(false)}>Cancel {props.entityTypeName} Update</Button>

                                        <Button
                                            style={{ width: "100%" }}
                                            onClick={
                                                () => {
                                                    props.entityChangeHandler(undefined);
                                                    setShowEntityChooser(false);
                                                }}>Set to None</Button>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </>
                    }
                </div>
            </Col>
        </Row >
    );

    return (
        <>{showSelectableEntity(props.selectedEntityRef)}</>
    );
}

export default DPSEntitySelector;